/**
 * Compass tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const compassTattoosPack: ToilePack = {
    slug: 'compass-tattoos',
    category: 'tattoo',
    name: 'Compass tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'rose', label: 'Compass rose', subject: 'an eight-pointed compass rose with the north point longer than the rest, a ring of degree marks around it'},
        {key: 'map', label: 'Compass and map', subject: 'a brass pocket compass lying open on a curled sheet of an old map with a coastline and a dotted route'},
        {key: 'floral', label: 'Compass and roses', subject: 'a round compass with two roses and leaves curving around its lower half'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['an anchor', 'red ink', 'shoulder'],
    directionHint: 'try: an anchor',
    seo: {
        title: 'Compass tattoo designs: rose, compass on a map, roses',
        metaDescription: 'Nine compass tattoo designs: a nautical compass rose, a compass on an old map and a compass in a frame of roses, in three styles.',
        h1: 'Compass tattoo designs: compass rose, compass on a map, compass and roses',
        tagline: 'Three compasses, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Compass tattoo designs, worn by travellers and by people who want a reminder of where home is, often on the forearm or the shoulder. The nine sheets show an eight-pointed compass rose, a brass compass open on an old map, and a round compass with two roses curving round it. Each is drawn as traditional flash, fine line and dotwork. Type a different navigation object or twist, such as an anchor, and the same styles can be asked for.',
        useCases: [
            'a shoulder or forearm compass rose',
            'a travel piece with an old map',
            'a compass framed by flowers',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine compass designs: a compass rose, a compass on a map and a compass with roses, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for an anchor or a different object?', a: 'Yes. Type a different navigation object or twist, such as an anchor; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
